import React from 'react';
import { ReactNode } from 'react';
import { Button } from '@/components/ui/button';

interface AgentPageLayoutProps {
  icon: ReactNode;
  title: string;
  subtitle: string;
  children: ReactNode;
}

export const AgentPageLayout: React.FC<AgentPageLayoutProps> = ({ icon, title, subtitle, children }) => {
  return (
    <div className="w-full max-w-4xl mx-auto">
      <header className="mb-8">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-lg bg-secondary/10 flex items-center justify-center">
            {icon}
          </div>
          <h1 className="text-3xl font-bold text-foreground">{title}</h1>
        </div>
        <p className="text-muted-foreground text-lg">{subtitle}</p>
      </header>
      
      <main>
        {children}
      </main>
    </div>
  );
};

export const AgentFormSection: React.FC<{ children: ReactNode }> = ({ children }) => {
  return (
    <section className="bg-card border border-border rounded-lg p-6 mb-6">
      {children}
    </section>
  );
};

export const AgentFormGroup: React.FC<{ children: ReactNode }> = ({ children }) => {
  return <div className="space-y-2">{children}</div>;
};

export const AgentLabel: React.FC<{ htmlFor: string; children: ReactNode }> = ({ htmlFor, children }) => {
  return (
    <label htmlFor={htmlFor} className="block text-sm font-medium text-foreground">
      {children}
    </label>
  );
};

export const AgentInput: React.FC<React.InputHTMLAttributes<HTMLInputElement>> = ({ className = '', ...props }) => {
  return (
    <input
      className={`w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-secondary focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed ${className}`}
      {...props}
    />
  );
};

export const AgentTextarea: React.FC<React.TextareaHTMLAttributes<HTMLTextAreaElement>> = ({ className = '', ...props }) => {
  return (
    <textarea
      className={`w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-secondary focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed resize-none ${className}`}
      {...props}
    />
  );
};

export const AgentSelect: React.FC<React.SelectHTMLAttributes<HTMLSelectElement>> = ({ className = '', ...props }) => {
  return (
    <select
      className={`w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-secondary focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed ${className}`}
      {...props}
    />
  );
};

export const AgentButton: React.FC<React.ButtonHTMLAttributes<HTMLButtonElement> & { variant?: 'primary' | 'secondary' }> = ({ 
  children, 
  className = '', 
  variant = 'primary',
  ...props 
}) => {
  return (
    <Button 
      variant={variant === 'primary' ? 'hero' : 'outline'}
      className={className}
      {...props}
    >
      {children}
    </Button>
  );
};

export const AgentError: React.FC<{ message: string; onRetry?: () => void }> = ({ message, onRetry }) => {
  return (
    <div className="mt-6 p-4 bg-destructive/10 border border-destructive/50 rounded-lg">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <p className="text-destructive text-sm flex-1">{message}</p>
        {onRetry && (
          <Button variant="outline" size="sm" onClick={onRetry} className="shrink-0">
            Tentar novamente
          </Button>
        )}
      </div>
    </div>
  );
};

export const AgentResult: React.FC<{ title: string; children: ReactNode }> = ({ title, children }) => {
  return (
    <section className="mt-8">
      <div className="bg-card border border-border rounded-lg p-6">
        <h2 className="text-2xl font-semibold text-foreground mb-4">{title}</h2>
        <div className="prose prose-invert max-w-none text-foreground">
          {children}
        </div>
      </div>
    </section>
  );
};

export const AgentDisclaimer: React.FC = () => {
  return (
    <div 
      className="mt-6 p-4 rounded-lg text-white"
      style={{ backgroundColor: '#5D4037' }}
    >
      <div className="flex items-start gap-3">
        <span className="text-xl font-bold">⚠️</span>
        <div className="flex-1 text-sm leading-relaxed">
          <p className="font-semibold mb-2">Este conteúdo foi gerado com base em princípios bíblicos amplamente reconhecidos, mas pode refletir perspectivas diferentes entre as denominações evangélicas.</p>
          <p>Sempre consulte a sua liderança espiritual e a comunidade de fé para aprofundar o discernimento sobre o tema.</p>
        </div>
      </div>
    </div>
  );
};

